/**
 * Enterprise Initiative Tracker — shared contract.
 *
 * Consumed by both the API (`apps/api/src/domains/initiatives`) and the
 * frontend (`apps/frontend/src/pages/initiatives`). Vocabularies here are the
 * single source of truth: the API validates against them, the frontend filters
 * and labels from them.
 *
 * ⚠️ Naming: `InitiativeRole` and `InitiativeEntry` are already exported by
 * `./techProgression` through the same `export *` barrel and mean something
 * unrelated (a technician's role on a tech-progression initiative). Do not add
 * either name here — it would be a duplicate-export compile error.
 *
 * ⚠️ This module exports RUNTIME values (the role tiers and vocabulary arrays),
 * not just types. Importing it from the frontend therefore requires
 * `@jmtest/shared-types` to resolve at runtime — see the vitest alias in
 * `apps/frontend/vitest.config.ts` and the package build step in CI.
 */

// ── Access control ───────────────────────────────────────────────────────────

/**
 * Roles that may READ the portfolio.
 *
 * Deliberately broad. The brief's "truth over vanity — red is a feature, not a
 * failure" only holds if the organisation can actually see the truth. Rows
 * marked `visibility: 'restricted'` are filtered separately, in the service
 * layer — breadth here is not a licence to expose sensitive initiatives.
 */
export const INITIATIVES_READ_TIER = [
  'Atlas.User',
  'Atlas.Manager',
  'Atlas.HR',
  'Atlas.HRBP',
  'Atlas.Executive',
  'Atlas.Admin',
] as const;

/**
 * Roles that may CREATE initiatives and edit ones they own or sponsor.
 *
 * This is a coarse route-level filter only. The service layer additionally
 * allows any caller whose Entra OID matches the row's `ownerOid` or
 * `sponsorOid`, so a non-manager owner can still update their own initiative.
 * Two layers, by design — see the route file's header comment.
 */
export const INITIATIVES_WRITE_TIER = [
  'Atlas.Manager',
  'Atlas.Executive',
  'Atlas.Admin',
] as const;

/**
 * Roles that may edit ANY initiative, apply AI-proposed changesets,
 * reassign owners, cancel, and read `visibility: 'restricted'` rows.
 */
export const INITIATIVES_ADMIN_TIER = ['Atlas.Executive', 'Atlas.Admin'] as const;

export type InitiativesReadRole = (typeof INITIATIVES_READ_TIER)[number];
export type InitiativesWriteRole = (typeof INITIATIVES_WRITE_TIER)[number];
export type InitiativesAdminRole = (typeof INITIATIVES_ADMIN_TIER)[number];

// ── Strategy pillars ─────────────────────────────────────────────────────────

/**
 * The five strategic pillars ("5 P's") an initiative can be aligned to.
 *
 * Stable slugs, deliberately decoupled from the presentational copy in
 * `apps/frontend/src/config/strategyPillars.ts`. The copy can be reworded — or
 * later moved into the `strategy_contents` table — without invalidating stored
 * initiative references, because this union is the contract either way.
 */
export type StrategyPillarId =
  | 'people-first'
  | 'customer-experience'
  | 'profitable-growth'
  | 'productivity'
  | 'pioneer';

export const STRATEGY_PILLAR_IDS = [
  'people-first',
  'customer-experience',
  'profitable-growth',
  'productivity',
  'pioneer',
] as const satisfies readonly StrategyPillarId[];

// ── Initiative vocabularies ──────────────────────────────────────────────────

/**
 * Lifecycle stage.
 *
 * Note `blocked` is retained for continuity with the brief, but **open blockers
 * are the source of truth** for whether an initiative is blocked — the API
 * derives an `isBlocked` flag from them and requires at least one open blocker
 * before allowing a transition *to* this stage. An initiative can legitimately
 * be `in_progress` and blocked at the same time.
 */
export type InitiativeStage =
  | 'idea'
  | 'planned'
  | 'in_progress'
  | 'blocked'
  | 'on_hold'
  | 'done'
  | 'cancelled';

export const INITIATIVE_STAGES = [
  'idea',
  'planned',
  'in_progress',
  'blocked',
  'on_hold',
  'done',
  'cancelled',
] as const satisfies readonly InitiativeStage[];

/**
 * Overall health. A one-line reason is REQUIRED whenever this is `amber` or
 * `red`, enforced on every write while in that state — not only on the
 * transition into it.
 */
export type InitiativeRagStatus = 'green' | 'amber' | 'red';

export const INITIATIVE_RAG_STATUSES = [
  'green',
  'amber',
  'red',
] as const satisfies readonly InitiativeRagStatus[];

/** Drives which metric set applies — `technology` unlocks DORA. */
export type InitiativeType =
  | 'technology'
  | 'operational'
  | 'strategic'
  | 'compliance'
  | 'other';

export const INITIATIVE_TYPES = [
  'technology',
  'operational',
  'strategic',
  'compliance',
  'other',
] as const satisfies readonly InitiativeType[];

export type InitiativePriority = 'p1' | 'p2' | 'p3';

export const INITIATIVE_PRIORITIES = [
  'p1',
  'p2',
  'p3',
] as const satisfies readonly InitiativePriority[];

/**
 * Who can see the initiative at all.
 *
 * `company` — everyone in {@link INITIATIVES_READ_TIER}.
 * `restricted` — {@link INITIATIVES_ADMIN_TIER} plus the named owner and
 * sponsor only. For M&A, personnel and compensation work.
 */
export type InitiativeVisibility = 'company' | 'restricted';

export const INITIATIVE_VISIBILITIES = [
  'company',
  'restricted',
] as const satisfies readonly InitiativeVisibility[];

// ── Blocker vocabularies ─────────────────────────────────────────────────────

export type InitiativeBlockerSeverity = 'low' | 'medium' | 'high' | 'critical';

export const INITIATIVE_BLOCKER_SEVERITIES = [
  'low',
  'medium',
  'high',
  'critical',
] as const satisfies readonly InitiativeBlockerSeverity[];

/** What is actually needed to clear the blocker. */
export type InitiativeBlockerNeedType =
  | 'decision'
  | 'approval'
  | 'resource'
  | 'info';

export const INITIATIVE_BLOCKER_NEED_TYPES = [
  'decision',
  'approval',
  'resource',
  'info',
] as const satisfies readonly InitiativeBlockerNeedType[];

// ── Provenance ───────────────────────────────────────────────────────────────

/**
 * How a change reached the record.
 *
 * The *actor* on an audit row is always the human who made or approved the
 * change — never "the AI". AI involvement is an attribute of a person's action,
 * which is both the honest model and what keeps "who changed this?" answerable.
 */
export type InitiativeActorType =
  | 'manual'
  | 'ai_assisted'
  | 'import'
  | 'system';

export const INITIATIVE_ACTOR_TYPES = [
  'manual',
  'ai_assisted',
  'import',
  'system',
] as const satisfies readonly InitiativeActorType[];
